import { BusinessDayCalculatorPL, CommunityTransactionForSaleMixin, CompanyParametersQuery, ContextKeys, DocumentExchangeRateValidationBoPart, DocumentItemQuantityGreaterThanInventoryLevelError, DocumentItemQuantityGreaterThanInventoryLevelWarning, DocumentItemsNumberingBoPart, DocumentNumberBoPart, DocumentVatRateForSaleValidationBoPart, DocumentWstoBoPart, ExchangeRateMixin, InventoryKindQuery, InventoryQuery, LoggedUserQuery, NumberingQuery, ProductInventoryLevelQuery, SaleDocumentAdjustmentRepositoryBase, UserCollectionQuery, VatRateForPurchaseQuery, propertyChangedJoinPointFilter, rewriteAddress, rewriteDocumentContractor, rewriteDocumentItem, rewriteExchangeRate } from '@skylab-shared/business';
import { BO, BusinessObjectBase, PropertyChangedJoinPoint, QueryBus, ResponseConflictObject, Weaver } from '@skylab-shared/core';
import { AddressModel, CalculationMethod, Company, ExchangeRateModel, InventoryDocumentItemModel, InventoryGoodsIssueAdjustmentDocumentModel, InventoryGoodsIssueDocumentModel, InventoryRelationshipCreationMethod, RelatedEntity } from '@skylab-shared/dataModel';
import { dateDiffInDays, head, not, substractDays } from '@skylab-shared/utils';
import { ProductRepository } from '../product/product.repo';
import { InventoryDocumentCalculatorBoPart } from './bo-parts/inventory-document-calculator.bo-part';
import { InventoryDocumentItemsValidationsBoPart } from './bo-parts/inventory-document-items-validations.bo-part';
import { InventoryDocumentErrorCodes, RequestedQuantityOfProductNotPresentInInventoryErrorObject } from './common/inventory-document-error-codes';
import { InventoryLinksMixin } from './mixins/inventory-links.mixin';
import { InventoryDocumentAssociatedWithGoodsIssueQuery } from './queries/inventory-document-associated-with-goods-issue.query';
import { refreshInventoryIssueDocumentItemPriceAsync } from './utils/inventory-document-item-utils';
import { InventoryGoodsIssueAdjustmentGoodsIssueDateEarlierThanAdjustedError } from './violations/inventory-goods-issue-adjustment-goods-issue-date-earlier-than-adjusted-error';
import { InventoryGoodsIssueAdjustmentIssueDateEarlierThanAdjustedError } from './violations/inventory-goods-issue-adjustment-issue-date-earlier-than-adjusted-error';

export interface InventoryGoodsIssueAdjustmentDocumentBO extends InventoryLinksMixin<InventoryGoodsIssueAdjustmentDocumentModel>, ExchangeRateMixin<InventoryGoodsIssueAdjustmentDocumentModel>, CommunityTransactionForSaleMixin<InventoryGoodsIssueAdjustmentDocumentModel> {};

@BO({
    modelType: InventoryGoodsIssueAdjustmentDocumentModel,
    mixins: [InventoryLinksMixin, ExchangeRateMixin, CommunityTransactionForSaleMixin]
})
export class InventoryGoodsIssueAdjustmentDocumentBO extends BusinessObjectBase<InventoryGoodsIssueAdjustmentDocumentModel> {
    private itemsQuantityOnOriginalDocument: Map<InventoryDocumentItemModel, number>;

    public constructor(
        weaver: Weaver,
        protected readonly productRepository: ProductRepository,
        protected readonly queryBus: QueryBus,
        protected readonly saleDocumentAdjustmentRepository: SaleDocumentAdjustmentRepositoryBase
    ) {
        super(weaver);
        this.initBOParts();
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.all([
            this.localContext.preload(NumberingQuery),
            this.localContext.preload(VatRateForPurchaseQuery),
            this.localContext.preload(CompanyParametersQuery),
            this.localContext.preload(LoggedUserQuery),
            this.localContext.preload(InventoryKindQuery),
            this.localContext.preload(InventoryQuery)
        ]);

        if (this.model.isPersistent) {
            await this.loadLinksAsync();
            this.itemsQuantityOnOriginalDocument = new Map(this.model.items.map(item => [item, item.quantity]));
            this.isGoodsIssueAdjustmentAssociatedWithSaleDocument = !!this.linksCommercial;
        }

        await this.setDocumentAssociatedWithGoodsIssueAsync();

        this.registerDocumentAdjustedReadonlyRule();
        this.registerDateValidationRules();
        this.registerItemRules();

        this.registerServerRuleViolationAfterGoodsIssueDateChange();
    }

    protected override handleConflictSaveResponse(conflict: ResponseConflictObject<InventoryDocumentErrorCodes, RequestedQuantityOfProductNotPresentInInventoryErrorObject>) {
        if ((conflict.code === InventoryDocumentErrorCodes.RequestedQuantityOfProductIsNotAvailable)) {
            const productIds = conflict.objectProperties?.filter((elem): elem is RequestedQuantityOfProductNotPresentInInventoryErrorObject => typeof elem !== 'string').map(elem => elem.productId);

            this.model.items.forEach(elem => {
                if (!productIds?.includes(elem.productId!)) return;
                this.addExternalRuleViolations<InventoryDocumentItemModel>({
                    [elem.sun]: [{
                        property: 'quantity',
                        violation: new DocumentItemQuantityGreaterThanInventoryLevelError()
                    }]
                });
                this.validation.queueValidationByViolationType(DocumentItemQuantityGreaterThanInventoryLevelWarning);
            });
        }
    }

    private initBOParts(): void {
        this.parts.push(...[
            new DocumentNumberBoPart(this.weaver, this.queryBus),
            new InventoryDocumentCalculatorBoPart(this.weaver),
            new DocumentItemsNumberingBoPart(this.weaver),
            new InventoryDocumentItemsValidationsBoPart(this.weaver),
            new DocumentExchangeRateValidationBoPart(this.weaver),
            new DocumentWstoBoPart(this.weaver, this.queryBus),
            new DocumentVatRateForSaleValidationBoPart(this.weaver)
        ]);
    }

    private registerServerRuleViolationAfterGoodsIssueDateChange(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(InventoryGoodsIssueDocumentModel, ['goodsIssueDate'])(
            () => this.removeServerRuleViolations()
        )));
    }

    private registerDateValidationRules(): void {
        this.validation.addRule(
            [[InventoryGoodsIssueAdjustmentDocumentModel, 'issueDate']],
            ctx => this.adjustedDocument ? dateDiffInDays(ctx.source.issueDate, this.adjustedDocument.issueDate) >= 0 : true,
            InventoryGoodsIssueAdjustmentIssueDateEarlierThanAdjustedError
        );

        this.validation.addRule(
            [[InventoryGoodsIssueAdjustmentDocumentModel, 'goodsIssueDate']],
            ctx => this.adjustedDocument ? dateDiffInDays(ctx.source.goodsIssueDate, this.adjustedDocument.goodsIssueDate) >= 0 : true,
            InventoryGoodsIssueAdjustmentGoodsIssueDateEarlierThanAdjustedError
        );
    }

    private registerDocumentAdjustedReadonlyRule(): void {
        const modelType = InventoryGoodsIssueAdjustmentDocumentModel;

        this.readonly.setAsReadonly([modelType, 'calculationMethod']);
        this.readonly.setAsReadonly([modelType, 'currencyId']);
        this.readonly.setAsReadonly([modelType, 'inventoryId']);
        this.readonly.setReadonlyOnSubtree(ExchangeRateModel, () => true);

        this.readonly.addRule([[modelType, 'client']], ctx => {
            return this.isGoodsIssueAdjustmentAssociatedWithSaleDocument;
        });

        this.readonly.addRule([
            [modelType, 'documentNumber'],
            [modelType, 'issueDate'],
            [modelType, 'issuer'],
            [modelType, 'numberingId'],
            [modelType, 'goodsIssueDate'],
            [modelType, 'recipient'],
            [modelType, 'transactionLocation'],
            [modelType, 'communityTransactionId'],
            [modelType, 'wstoAdditionalInformation'],
            [modelType, 'wstoConsumerCountryId'],
            [modelType, 'wstoShipmentCountryId']
        ], () => this.isDocumentAdjusted || !!this.linksCommercial || this.isGoodsIssueAdjustmentAssociatedWithSaleDocument || this.isDocumentAssociatedWithGoodsIssue);

        this.readonly.setReadonlyOnSubtree(AddressModel, () => this.isDocumentAdjusted || !!this.linksCommercial || this.isGoodsIssueAdjustmentAssociatedWithSaleDocument || this.isDocumentAssociatedWithGoodsIssue);
    }

    private registerItemRules(): void {
        this.readonly.addRule(
            [[InventoryDocumentItemModel, 'productId']],
            [[InventoryDocumentItemModel, 'productName'], [InventoryDocumentItemModel, 'unitId']],
            ctx => not(!ctx.source.productId)
        );

        this.readonly.setReadonlyOnSubtree(InventoryDocumentItemModel, () => this.isDocumentAdjusted || !!this.linksCommercial || this.isGoodsIssueAdjustmentAssociatedWithSaleDocument || this.isDocumentAssociatedWithGoodsIssue);

        this.validation.addRule(
            [[InventoryDocumentItemModel, 'quantity']],
            async ctx => {
                if (ctx.source.quantity < 0) return true;

                if (this.validationResult.find(elem => (elem.ruleViolation.violation instanceof DocumentItemQuantityGreaterThanInventoryLevelError)
                    && elem.source === ctx.source)) return true;

                const validate = async (previousQuantity: number) => {
                    if (ctx.source.quantity <= previousQuantity) return true;

                    const inventoryLevel = await this.getProductInventoryLevel(ctx.source);
                    if (inventoryLevel == null) return true;
                    const inventoryLevelAfterAdjustment = (inventoryLevel - (ctx.source.quantity - previousQuantity));
                    return inventoryLevelAfterAdjustment >= 0;
                };

                const adjustedItem = this.adjustedDocument?.items.find(elem => elem.ordinalNumber === ctx.source.ordinalNumber);
                const editedItemQuantity = this.itemsQuantityOnOriginalDocument?.get(ctx.source);

                if (!this.model.isPersistent && adjustedItem)
                    return await validate(adjustedItem.quantity);

                if (this.model.isPersistent && editedItemQuantity)
                    return await validate(editedItemQuantity);

                if (!adjustedItem && !editedItemQuantity) {
                    const inventoryLevel = ctx.source.inventoryLevel ?? await this.getProductInventoryLevel(ctx.source);
                    if (inventoryLevel == null) return true;
                    return ctx.source.quantity <= inventoryLevel;
                }

                return true;
            },
            DocumentItemQuantityGreaterThanInventoryLevelWarning
        );
    }

    private async getProductInventoryLevel(item: InventoryDocumentItemModel): Promise<number | null> {
        const productInventoryData = await this.localContext.getAsync(ContextKeys.productInventoryLevel(item.productId!), () => this.queryBus.queryAsync(new ProductInventoryLevelQuery(item.productId!)));
        if (!productInventoryData) return null;

        return productInventoryData.quantity;
    }

    public isGoodsIssueAdjustmentAssociatedWithSaleDocument: boolean;

    protected calculateDateForCurrencyExchangeRate(): Date {
        const date = substractDays(this.model.goodsIssueDate, 1);
        return BusinessDayCalculatorPL.getLastBusinessDay(date);
    }

    public async refreshDocumentItemsPricesAsync(): Promise<void> {
        await Promise.all(this.model.items.map(async item => await refreshInventoryIssueDocumentItemPriceAsync(item, this.localContext, this.model, this.productRepository)));
    }

    private _adjustedDocument: InventoryGoodsIssueDocumentModel | InventoryGoodsIssueAdjustmentDocumentModel;
    public set adjustedDocument(document: InventoryGoodsIssueDocumentModel | InventoryGoodsIssueAdjustmentDocumentModel) {
        this._adjustedDocument = document;
    }

    public get adjustedDocument() {
        return this._adjustedDocument;
    }

    private _isDocumentAssociatedWithGoodsIssue: boolean;
    public get isDocumentAssociatedWithGoodsIssue(): boolean {
        return this._isDocumentAssociatedWithGoodsIssue;
    }
    private async setDocumentAssociatedWithGoodsIssueAsync(): Promise<void> {
        this._isDocumentAssociatedWithGoodsIssue = this.model.isPersistent
            ? await this.queryBus.queryAsync(new InventoryDocumentAssociatedWithGoodsIssueQuery(this.model.id))
            : false;
    }

    public async fillGoodsIssueAdjustmentToSaleDocumentAdjustmentAsync(saleDocumentId: number, creationMethod: InventoryRelationshipCreationMethod): Promise<void> {
        this.isGoodsIssueAdjustmentAssociatedWithSaleDocument = true;

        this.model.adjustedDocumentId = this.adjustedDocument.id;
        this.model.adjustedDocumentVersion = this.adjustedDocument.version;

        const { city }: Company = this.localContext.getPreloaded(ContextKeys.company)!;
        const { id: loggedUserId } = this.localContext.getPreloaded(LoggedUserQuery)!;
        const loggedUser = this.localContext.firstPreloaded(UserCollectionQuery, { id: loggedUserId })!;

        const numberings = this.localContext.getPreloaded(NumberingQuery)!;
        const [{ id: defaultNumberingId }] = numberings.filter(n => n.default && n.kinds.indexOf(this.model.kind) > -1);

        const config = this.localContext.firstPreloaded(InventoryKindQuery, { name: this.model.kind })!;
        const inventory = head(this.localContext.getPreloaded(InventoryQuery)!);

        const saleDocument = await this.saleDocumentAdjustmentRepository.findAsync(saleDocumentId);

        this.model.numberingId = defaultNumberingId;
        this.model.issuedByVatPayer = saleDocument.issuedByVatPayer;
        this.model.calculationMethod = saleDocument.calculationMethod as CalculationMethod.Gross | CalculationMethod.Net;
        this.model.transactionLocation = config.useCustomTransactionLocation ? config.customTransactionLocation : city;
        this.model.issuer = `${loggedUser.firstName} ${loggedUser.lastName}`;
        this.model.recipient = saleDocument.recipient;
        this.model.communityTransactionId = saleDocument.communityTransactionId;

        const inventoryDate = saleDocument.goodsIssueDate || saleDocument.saleDate;
        this.model.issueDate = new Date(inventoryDate);
        this.model.goodsIssueDate = new Date(inventoryDate);

        this.model.currencyId = saleDocument.currencyId;

        this.model.wstoAdditionalInformation = saleDocument.wstoAdditionalInformation;
        this.model.wstoConsumerCountryId = saleDocument.wstoConsumerCountryId;
        this.model.wstoShipmentCountryId = saleDocument.wstoShipmentCountryId;

        this.model.creationMethod = creationMethod;

        if (saleDocument.exchangeRate) {
            const exchangeRate = new ExchangeRateModel();
            this.model.exchangeRate = exchangeRate;
            rewriteExchangeRate(exchangeRate, saleDocument.exchangeRate);
        }

        rewriteDocumentContractor(this.model, saleDocument)

        if (inventory) {
            this.model.inventoryId = inventory.id;
        }

        if (saleDocument.deliveryAddress) {
            const deliveryAddress = new AddressModel();
            this.model.deliveryAddress = deliveryAddress;
            rewriteAddress(deliveryAddress, saleDocument.deliveryAddress);
        }

        saleDocument.items.filter(elem => elem.includesInventoryControl).sort((i1, i2) => i1.ordinalNumber - i2.ordinalNumber).forEach(item => {
            const model = new InventoryDocumentItemModel();
            this.model.items.push(model);
            rewriteDocumentItem(model, item);
            model.relatedCommercialItemsIds = item.id ? [item.id] : [];
            model.note = null;
            return model;
        });

        this.model.items.forEach(item => {
            const adjustedItem = this.adjustedDocument.items.find(i => i.ordinalNumber === item.ordinalNumber);
            if (adjustedItem) {
                item.adjustedItemId = adjustedItem.id ?? null;
            }
        });

        this.model.relatedCommercialDocuments = [RelatedEntity.from(saleDocument)];
    }
}
